import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useMovePage } from '../../hooks/usePage';
import { fetchPages } from '../../api/pageApi';
import { fetchWorkspaces } from '../../redux/Slices/workspaceSlice';
import { updatePageFromSocket } from '../../redux/Slices/pageSlice';
import {
  Close,
  ChevronRight,
  ExpandMore,
  Folder,
  Business,
  Person,
  Check
} from '../icons';
import './scss/MovePageDialog.scss';


/**
 * Move Page Dialog
 * Allows moving a page to a different parent or workspace
 */
const MovePageDialog = ({ page, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const [selectedParentId, setSelectedParentId] = useState(page.parentId || null);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(page.workspaceId || null);
  const [expandedPages, setExpandedPages] = useState(new Set());
  const [pages, setPages] = useState([]);

  const movePageMutation = useMovePage();

  // Get workspaces from Redux
  const { workspaces } = useSelector(state => state.workspaces);

  // Fetch workspaces if not loaded
  useEffect(() => {
    if (workspaces.length === 0) {
      dispatch(fetchWorkspaces({ useCache: true }));
    }
  }, [dispatch, workspaces.length]);

  // Fetch pages for selected workspace
  useEffect(() => {
    if (!selectedWorkspaceId) {
      setPages([]);
      return;
    }

    const loadPages = async () => {
      try {
        const result = await fetchPages(selectedWorkspaceId);
        setPages(result?.pages || []);
      } catch (error) {
        console.error('Error loading pages:', error);
        setPages([]);
      }
    };

    loadPages();
  }, [selectedWorkspaceId]);

  // Helper function to check if a page is a descendant of another page
  const isDescendant = (potentialDescendant, ancestorId, allPages) => {
    if (!potentialDescendant.parentId) return false;
    if (potentialDescendant.parentId === ancestorId) return true;

    const parent = allPages.find(p => p._id === potentialDescendant.parentId);
    if (!parent) return false;

    return isDescendant(parent, ancestorId, allPages);
  };

  // Filter out the current page and its descendants to prevent circular references
  const availablePages = pages.filter(p => {
    if (p._id === page._id) return false;
    // Check if p is a descendant of the page being moved
    if (isDescendant(p, page._id, pages)) return false;
    return true;
  });

  const rootPages = availablePages.filter(p => !p.parentId && !p.deletedAt);

  const handleToggleExpand = (pageId) => {
    const newExpanded = new Set(expandedPages);
    if (newExpanded.has(pageId)) {
      newExpanded.delete(pageId);
    } else {
      newExpanded.add(pageId);
    }
    setExpandedPages(newExpanded);
  };

  const handleSelectLocation = (parentId, workspaceId) => {
    setSelectedParentId(parentId);
    setSelectedWorkspaceId(workspaceId);
  };

  const handleMove = async () => {
    // Optimistic update: update parent/workspace locally
    const originalParentId = page.parentId || null;
    const originalWorkspaceId = page.workspaceId || null;

    try {
      // Apply optimistic change
      dispatch(updatePageFromSocket({
        ...page,
        parentId: selectedParentId || null,
        workspaceId: selectedWorkspaceId || null,
      }));

      // Call API
      await movePageMutation.mutateAsync({
        pageId: page.shortId,
        moveData: {
          newParentId: selectedParentId,
          newWorkspaceId: selectedWorkspaceId,
        },
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      // Revert optimistic change on error
      dispatch(updatePageFromSocket({
        ...page,
        parentId: originalParentId,
        workspaceId: originalWorkspaceId,
      }));
      console.error('Failed to move page:', error);
    }
  };

  const renderPageTree = (parentPages, level = 0) => {
    return parentPages.map(p => {
      const childPages = availablePages.filter(child => child.parentId === p._id);
      const hasChildren = childPages.length > 0;
      const isExpanded = expandedPages.has(p._id);
      const isSelected = selectedParentId === p._id;

      return (
        <div key={p._id} className="move-page-tree-item">
          <div
            className={`move-page-item ${isSelected ? 'selected' : ''}`}
            style={{ paddingLeft: `${level * 20 + 12}px` }}
            onClick={() => handleSelectLocation(p._id, p.workspaceId)}
          >
            {hasChildren ? (
              <button
                className="move-page-toggle"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleExpand(p._id);
                }}
              >
                {isExpanded ? (
                  <ExpandMore size={16} />
                ) : (
                  <ChevronRight size={16} />
                )}
              </button>
            ) : (
              <div className="move-page-toggle-spacer" />
            )}
            
            <span className="move-page-icon">{p.icon || '📄'}</span>
            <span className="move-page-title">{p.title || 'Untitled'}</span>
            
            {isSelected && (
              <Check size={16} className="move-page-check" />
            )}
          </div>

          {isExpanded && hasChildren && (
            <div className="move-page-children">
              {renderPageTree(childPages, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div className="move-page-dialog-overlay" onClick={onClose}>
      <div className="move-page-dialog" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="move-dialog-header">
          <h3>Move "{page.title || 'Untitled'}"</h3>
          <button className="move-dialog-close" onClick={onClose}>
            <Close size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="move-dialog-content">
          <p className="move-dialog-description">
            Choose a new location for this page
          </p>

          {/* Workspace selector */}
          <div className="move-workspace-selector">
            <h4>Workspace</h4>
            <div className="move-workspace-list">
              {/* Personal option */}
              <button
                className={`move-workspace-item ${selectedWorkspaceId === null ? 'selected' : ''}`}
                onClick={() => handleSelectLocation(null, null)}
              >
                <Person size={18} />
                <span>Personal</span>
                {selectedWorkspaceId === null && (
                  <Check size={16} className="workspace-check" />
                )}
              </button>

              {workspaces.map(workspace => (
                <button
                  key={workspace._id}
                  className={`move-workspace-item ${selectedWorkspaceId === workspace._id ? 'selected' : ''}`}
                  onClick={() => handleSelectLocation(null, workspace._id)}
                >
                  <Business size={18} />
                  <span>{workspace.name}</span>
                  {selectedWorkspaceId === workspace._id && (
                    <Check size={16} className="workspace-check" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Parent page selector */}
          {selectedWorkspaceId && (
            <div className="move-parent-selector">
              <h4>Parent Page (Optional)</h4>
              <div className="move-page-tree">
                {/* Root option */}
                <div
                  className={`move-page-item ${selectedParentId === null ? 'selected' : ''}`}
                  onClick={() => handleSelectLocation(null, selectedWorkspaceId)}
                >
                  <div className="move-page-toggle-spacer" />
                  <Folder size={18} />
                  <span className="move-page-title">Root (No parent)</span>
                  {selectedParentId === null && (
                    <Check size={16} className="move-page-check" />
                  )}
                </div>

                {/* Page tree */}
                {rootPages.length > 0 && renderPageTree(rootPages)}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="move-dialog-footer">
          <button
            className="move-dialog-cancel"
            onClick={onClose}
            disabled={movePageMutation.isPending}
          >
            Cancel
          </button>
          <button
            className="move-dialog-confirm"
            onClick={handleMove}
            disabled={
              movePageMutation.isPending ||
              (selectedParentId === page.parentId && selectedWorkspaceId === page.workspaceId)
            }
          >
            {movePageMutation.isPending ? 'Moving...' : 'Move Page'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MovePageDialog;

